/**
 * Customer / Guarantor document uploader. Reuses /api/customers/{cid}/...
 * endpoints. Single click upload, shows current document if present.
 */
import React, { useState } from "react";
import { api } from "../lib/api";
import { Upload, FileCheck2, X, Eye } from "lucide-react";
import { toast } from "sonner";

const CUSTOMER_SLOTS = [
  ["photo", "Customer Photo"],
  ["cnic_front", "CNIC Front"],
  ["cnic_back", "CNIC Back"],
  ["selfie_cnic", "Selfie With CNIC"],
  ["utility_bill", "Utility Bill"],
  ["business_card", "Business Card (Optional)"],
];

const GUARANTOR_SLOTS = [
  ["photo", "Photo"],
  ["cnic_front", "CNIC Front"],
  ["cnic_back", "CNIC Back"],
];

function SlotUploader({ slotKey, label, currentUrl, onUpload, onClear, busy, fullUrlBase }) {
  const inputRef = React.useRef();
  return (
    <div className="border border-dashed border-slate-300 rounded-md p-3 bg-slate-50" data-testid={`doc-slot-${slotKey}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</div>
        {currentUrl && (
          <a
            href={`${fullUrlBase}${currentUrl}`}
            target="_blank"
            rel="noreferrer"
            className="text-emerald-700 hover:text-emerald-800 text-xs font-semibold inline-flex items-center gap-1"
          >
            <Eye className="w-3 h-3" /> View
          </a>
        )}
      </div>
      {currentUrl ? (
        <div className="flex items-center gap-2">
          <FileCheck2 className="w-5 h-5 text-emerald-600" />
          <span className="text-xs text-slate-600 truncate flex-1">{currentUrl.split("/").pop()}</span>
          <button type="button" onClick={onClear} className="text-red-600 hover:text-red-800" title="Remove">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept="image/*,application/pdf"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = "";
            }}
            data-testid={`doc-file-${slotKey}`}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 text-sm font-semibold py-2 disabled:opacity-50"
            data-testid={`doc-upload-${slotKey}`}
          >
            <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload"}
          </button>
        </>
      )}
    </div>
  );
}

export default function DocumentsCard({ customerId, customer, onUpdated, fullUrlBase }) {
  const [busy, setBusy] = useState({});

  const upload = async (urlPath, slot, file) => {
    setBusy({ ...busy, [slot]: true });
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(urlPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${slot.replace(/_/g, " ")} uploaded`);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy({ ...busy, [slot]: false });
    }
  };

  const docs = customer?.documents || {};
  const g1docs = customer?.guarantor1?.documents || {};
  const g2docs = customer?.guarantor2?.documents || {};

  return (
    <div className="space-y-4" data-testid="documents-card">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="font-display font-semibold text-slate-900 mb-3">Customer Documents</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {CUSTOMER_SLOTS.map(([slot, label]) => (
            <SlotUploader
              key={slot}
              slotKey={`cust-${slot}`}
              label={label}
              currentUrl={docs[slot]}
              busy={busy[slot]}
              fullUrlBase={fullUrlBase}
              onUpload={(f) => upload(`/customers/${customerId}/documents/${slot}`, slot, f)}
              onClear={() => upload(`/customers/${customerId}/documents/${slot}`, slot, new File([""], "empty"))}
            />
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="font-display font-semibold text-slate-900 mb-3">Guarantor 1 Documents</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {GUARANTOR_SLOTS.map(([slot, label]) => (
            <SlotUploader
              key={`g1-${slot}`}
              slotKey={`g1-${slot}`}
              label={label}
              currentUrl={g1docs[slot]}
              busy={busy[`g1-${slot}`]}
              fullUrlBase={fullUrlBase}
              onUpload={(f) => upload(`/customers/${customerId}/guarantor/1/documents/${slot}`, `g1-${slot}`, f)}
              onClear={() => upload(`/customers/${customerId}/guarantor/1/documents/${slot}`, `g1-${slot}`, new File([""], "empty"))}
            />
          ))}
        </div>
      </div>

      {customer?.guarantor2 && (
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="font-display font-semibold text-slate-900 mb-3">Guarantor 2 Documents</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {GUARANTOR_SLOTS.map(([slot, label]) => (
              <SlotUploader
                key={`g2-${slot}`}
                slotKey={`g2-${slot}`}
                label={label}
                currentUrl={g2docs[slot]}
                busy={busy[`g2-${slot}`]}
                fullUrlBase={fullUrlBase}
                onUpload={(f) => upload(`/customers/${customerId}/guarantor/2/documents/${slot}`, `g2-${slot}`, f)}
                onClear={() => upload(`/customers/${customerId}/guarantor/2/documents/${slot}`, `g2-${slot}`, new File([""], "empty"))}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
